import React, { useState, useEffect } from 'react';
import planCuentasService from '../services/planCuentasService';
import './PlanCuentaModal.css';

const PlanCuentaModal = ({ cuenta, parent, cuentaActiva, onClose }) => {
  const isEdit = !!cuenta;
  const [formData, setFormData] = useState({
    cuenta: cuentaActiva,
    parent: parent?.id || null,
    codigo: '',
    nombre: '',
    tipo_cuenta: 'ACTIVO',
    activo: true,
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (cuenta) {
      setFormData({
        cuenta: cuenta.cuenta,
        parent: cuenta.parent,
        codigo: cuenta.codigo,
        nombre: cuenta.nombre,
        tipo_cuenta: cuenta.tipo_cuenta,
        activo: cuenta.activo,
      });
    } else if (parent) {
      // Sugerir código relativo para cuenta hija (solo el segmento, no el código completo)
      // El codigo_completo se calcula automáticamente en el backend
      setFormData(prev => ({
        ...prev,
        parent: parent.id,
        codigo: '1', // Código relativo sugerido
        tipo_cuenta: parent.tipo_cuenta, // Heredar tipo del padre
      }));
    }
  }, [cuenta, parent]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    // Limpiar error del campo
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.codigo.trim()) {
      newErrors.codigo = 'El código es requerido';
    }

    if (!formData.nombre.trim()) {
      newErrors.nombre = 'El nombre es requerido';
    }

    if (!formData.tipo_cuenta) {
      newErrors.tipo_cuenta = 'El tipo de cuenta es requerido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      setLoading(true);
      setErrors({});

      const dataToSend = {
        ...formData,
      };

      if (isEdit) {
        await planCuentasService.update(cuenta.id, dataToSend);
      } else {
        await planCuentasService.create(dataToSend);
      }

      onClose(true); // true indica que se guardó
    } catch (err) {
      console.error('Error al guardar cuenta:', err);

      if (err.response?.data) {
        // Errores de validación del backend
        setErrors(err.response.data);
      } else {
        setErrors({ general: 'Error al guardar la cuenta' });
      }
    } finally {
      setLoading(false);
    }
  };

  const getTiposCuenta = () => [
    { value: 'ACTIVO', label: 'Activo' },
    { value: 'PASIVO', label: 'Pasivo' },
    { value: 'PATRIMONIO', label: 'Patrimonio' },
    { value: 'INGRESO', label: 'Ingreso' },
    { value: 'GASTO', label: 'Gasto' },
  ];


  return (
    <div className="modal-overlay" onClick={() => onClose(false)}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEdit ? 'Editar Cuenta' : parent ? 'Nueva Cuenta Hija' : 'Nueva Cuenta Raíz'}</h2>
          <button className="modal-close" onClick={() => onClose(false)}>×</button>
        </div>

        {parent && (
          <div className="parent-info">
            <strong>Cuenta Padre:</strong> {parent.codigo_completo} - {parent.nombre}
            <br />
            <small>El código completo será: {parent.codigo_completo}.[código ingresado]</small>
          </div>
        )}

        <form onSubmit={handleSubmit} className="plan-cuenta-form">
          {errors.general && (
            <div className="form-error-general">{errors.general}</div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="codigo">Código *</label>
              <input
                type="text"
                id="codigo"
                name="codigo"
                value={formData.codigo}
                onChange={handleChange}
                className={errors.codigo ? 'error' : ''}
                placeholder={parent ? "Ej: 1, 2, 3..." : "Ej: 1, 2, 3..."}
              />
              {errors.codigo && <span className="error-message">{errors.codigo}</span>}
              {!parent && <small>Código para cuenta raíz</small>}
            </div>

            <div className="form-group">
              <label htmlFor="tipo_cuenta">Tipo de Cuenta *</label>
              <select
                id="tipo_cuenta"
                name="tipo_cuenta"
                value={formData.tipo_cuenta}
                onChange={handleChange}
                className={errors.tipo_cuenta ? 'error' : ''}
              >
                {getTiposCuenta().map(tipo => (
                  <option key={tipo.value} value={tipo.value}>
                    {tipo.label}
                  </option>
                ))}
              </select>
              {errors.tipo_cuenta && <span className="error-message">{errors.tipo_cuenta}</span>}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="nombre">Nombre *</label>
            <input
              type="text"
              id="nombre"
              name="nombre"
              value={formData.nombre}
              onChange={handleChange}
              className={errors.nombre ? 'error' : ''}
              placeholder="Ej: Caja, Bancos, Proveedores"
            />
            {errors.nombre && <span className="error-message">{errors.nombre}</span>}
          </div>

          <div className="form-row">
            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="activo"
                  checked={formData.activo}
                  onChange={handleChange}
                />
                <span>Cuenta Activa</span>
              </label>
            </div>
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => onClose(false)}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? 'Guardando...' : isEdit ? 'Actualizar' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PlanCuentaModal;
